import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Radio, ExternalLink } from "lucide-react";
import { useData } from "@/context/DataContext";
import { cn } from "@/lib/utils";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_novemberkasan-live/artifacts/wbdnhihh_Novemberkasan_Logotyp.png";

const NAV = [
  { to: "/tavlingsinformation", label: "Tävlingsinfo" },
  { to: "/for-forare", label: "Förare" },
  { to: "/depa-bokning", label: "Depåbokning" },
  { to: "/anslagstavla", label: "Anslagstavla", href: "https://padlet.com/ntstiming/anslagstavla-novemberkasan-m1b2b0zhq47ghx5c" },
  { to: "/videor", label: "Video" },
  { to: "/publik", label: "Publik" },
  { to: "/partners", label: "Partners" },
  { to: "/historia", label: "Historia" },
  { to: "/kontakt", label: "Kontakt" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { settings } = useData();
  const location = useLocation();
  const live = settings?.live_mode;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={cn(
          "fixed top-0 inset-x-0 z-50 transition-all duration-500",
          scrolled ? "bg-[#0B0C0E]/92 backdrop-blur-xl border-b border-white/10 py-3" : "bg-gradient-to-b from-black/70 to-transparent py-5"
        )}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-end gap-2 shrink-0">
            <img src={LOGO} alt="Novemberkåsan" className="h-12 sm:h-16 w-auto" />
            <span className="font-mono text-[10px] px-1.5 py-0.5 bg-amber-500 text-black rounded font-bold leading-none">2026</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            {NAV.map((n) => n.href ? (
              <a key={n.to} href={n.href} target="_blank" rel="noopener" data-testid={`nav-${n.to.replace(/\//g, "")}`}
                className="inline-flex items-center gap-1 font-mono text-[11px] xl:text-xs uppercase tracking-widest transition-colors hover:text-amber-500 text-white/85">
                {n.label} <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace(/\//g, "")}`}
                className={cn(
                  "font-mono text-[11px] xl:text-xs uppercase tracking-widest transition-colors hover:text-amber-500",
                  location.pathname === n.to ? "text-amber-500" : "text-white/85"
                )}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {live && (
              <Link to="/publik" data-testid="nav-live" className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
                <Radio className="w-3.5 h-3.5 pulse-dot" /> Live
              </Link>
            )}
            <a
              href={settings?.anmalan_url || "#anmalan"}
              data-testid="nav-anmalan"
              className="hidden sm:inline-flex items-center rounded-full bg-amber-500 px-5 py-2 text-xs font-bold uppercase tracking-widest text-black hover:bg-amber-400 transition-colors amber-glow"
            >
              Anmälan
            </a>
            <button data-testid="nav-hamburger" onClick={() => setOpen(true)} className="lg:hidden p-2 text-white">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div className={cn("fixed inset-0 z-[60] lg:hidden transition-opacity duration-300", open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
        <div className={cn("absolute right-0 top-0 h-full w-[82%] max-w-sm bg-[#0B0C0E] border-l border-white/10 transition-transform duration-300 flex flex-col", open ? "translate-x-0" : "translate-x-full")}>
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <span className="font-display text-lg font-black uppercase text-white">Meny</span>
            <button data-testid="nav-close" onClick={() => setOpen(false)} className="p-2 text-white/70"><X className="w-6 h-6" /></button>
          </div>
          <div className="p-5 grid grid-cols-2 gap-2">
            <a href={settings?.anmalan_url || "#anmalan"} onClick={() => setOpen(false)} className="rounded-lg bg-amber-500 px-3 py-3 text-center text-xs font-bold uppercase tracking-widest text-black">Anmälan</a>
            <Link to="/dokument" className="rounded-lg border border-white/15 px-3 py-3 text-center text-xs font-bold uppercase tracking-widest text-white">Startlista</Link>
            <Link to="/publik" className="rounded-lg border border-white/15 px-3 py-3 text-center text-xs font-bold uppercase tracking-widest text-white">Publikinfo</Link>
            <Link to="/publik" className="rounded-lg border border-white/15 px-3 py-3 text-center text-xs font-bold uppercase tracking-widest text-white flex items-center justify-center gap-1">
              {live && <Radio className="w-3.5 h-3.5 text-red-500 pulse-dot" />} Live
            </Link>
          </div>
          <nav className="flex-1 overflow-y-auto px-5 pb-8">
            {NAV.map((n) => n.href ? (
              <a key={n.to} href={n.href} target="_blank" rel="noopener" onClick={() => setOpen(false)} data-testid={`mnav-${n.to.replace(/\//g, "")}`} className="flex items-center gap-2 py-3.5 border-b border-white/8 font-display text-xl font-bold uppercase text-white hover:text-amber-500 transition-colors">
                {n.label} <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <Link key={n.to} to={n.to} data-testid={`mnav-${n.to.replace(/\//g, "")}`} className="block py-3.5 border-b border-white/8 font-display text-xl font-bold uppercase text-white hover:text-amber-500 transition-colors">
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};
